import React, { useEffect, useRef, useState } from 'react';
import { advanceQuiz, fetchQuestions, pauseQuizSession, resumeQuizSession, saveQuizProgress, submitAnswers } from '../api.js';
import QuestionCard from './QuestionCard.js';

function QuizPage({ sessionType, onFinish }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [questionRemaining, setQuestionRemaining] = useState(30);
  const [totalRemaining, setTotalRemaining] = useState(null);
  const [connection, setConnection] = useState('active');
  const [confirmation, setConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const answersRef = useRef([]);
  const currentIdxRef = useRef(0);
  const progressQueueRef = useRef(Promise.resolve());
  const advancingRef = useRef(false);
  const finishingRef = useRef(false);

  function finish(result, loadedQuestions = questions) {
    if (finishingRef.current) return;
    finishingRef.current = true;
    onFinish(result, loadedQuestions);
  }

  function applySession(session) {
    if (!session) return;
    setConnection(session.state === 'paused' ? 'paused' : 'active');
    setCurrentIdx(session.currentQuestionIndex);
    currentIdxRef.current = session.currentQuestionIndex;
    setQuestionRemaining(session.questionRemainingSeconds);
    setTotalRemaining(session.totalRemainingSeconds ?? null);
  }

  function handleFailure(requestError) {
    if (requestError?.data?.completed) return finish(requestError.data.result || {}, requestError.data.questions || questions);
    if ([401, 403].includes(requestError?.status)) {
      setConnection('blocked');
      setError(requestError.message || 'This quiz is active on another device.');
      return;
    }
    setConnection('reconnecting');
    setError(requestError.message || 'Connection lost. Your quiz is being paused and will resume when reconnected.');
  }

  async function loadQuiz() {
    setLoading(true);
    try {
      const data = await fetchQuestions();
      setQuestions(data.questions || []);
      answersRef.current = data.session?.answers || [];
      setAnswers(answersRef.current);
      applySession(data.session);
      setError('');
    } catch (requestError) {
      handleFailure(requestError);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadQuiz(); }, []);

  useEffect(() => {
    const warnBeforeLeaving = event => {
      pauseQuizSession();
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, []);

  useEffect(() => {
    if (connection !== 'reconnecting' && connection !== 'paused') return undefined;
    const reconnect = async () => {
      try {
        const data = await resumeQuizSession();
        applySession(data.session);
        setError('');
      } catch (requestError) {
        if (requestError?.data?.completed) finish(requestError.data.result || {}, requestError.data.questions || questions);
      }
    };
    reconnect();
    const timer = setInterval(reconnect, 5000);
    return () => clearInterval(timer);
  }, [connection]);

  useEffect(() => {
    if (!questions.length || connection !== 'active' || finishingRef.current) return undefined;
    const timer = setInterval(() => {
      setQuestionRemaining(value => Math.max(0, value - 1));
      if (totalRemaining !== null) setTotalRemaining(value => Math.max(0, value - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [questions.length, connection, totalRemaining]);

  useEffect(() => {
    if (questionRemaining !== 0 || connection !== 'active' || advancingRef.current) return;
    advanceQuestion(true);
  }, [questionRemaining, connection]);

  useEffect(() => {
    if (totalRemaining !== 0 || connection !== 'active') return;
    saveAndSubmit();
  }, [totalRemaining, connection]);

  useEffect(() => {
    if (!questions.length || connection !== 'active') return undefined;
    const timer = setInterval(() => persistProgress().catch(() => {}), 10000);
    return () => clearInterval(timer);
  }, [questions.length, connection]);

  function queueSessionRequest(work) {
    const request = progressQueueRef.current.then(work);
    progressQueueRef.current = request.catch(() => {});
    return request;
  }

  function persistProgress() {
    const answers = answersRef.current;
    const currentQuestionIndex = currentIdxRef.current;
    return queueSessionRequest(async () => {
      try {
        const data = await saveQuizProgress(answers, currentQuestionIndex);
        applySession(data.session);
        return data;
      } catch (requestError) {
        handleFailure(requestError);
        throw requestError;
      }
    });
  }

  function recordAnswer(questionIndex, answerIndex) {
    if (connection !== 'active') return;
    const nextAnswers = [...answersRef.current.filter(answer => answer.questionIndex !== questionIndex), { questionIndex, answerIndex }];
    answersRef.current = nextAnswers;
    setAnswers(nextAnswers);
    persistProgress().catch(() => {});
  }

  async function advanceQuestion(auto = false) {
    if (advancingRef.current || connection !== 'active') return;
    advancingRef.current = true;
    try {
      const expectedQuestionIndex = currentIdxRef.current;
      await persistProgress();
      const data = await queueSessionRequest(() => advanceQuiz(expectedQuestionIndex));
      if (data.completed) return finish(data.result || {});
      applySession(data.session);
      setError('');
    } catch (requestError) {
      if (!auto) setError(requestError.message || 'Unable to move to the next question.');
    } finally {
      advancingRef.current = false;
    }
  }

  async function saveAndSubmit() {
    if (submitting || finishingRef.current || connection !== 'active') return;
    setSubmitting(true);
    setError('');
    try {
      await persistProgress();
      finish(await queueSessionRequest(() => submitAnswers()));
    } catch (requestError) {
      handleFailure(requestError);
      setSubmitting(false);
    }
  }

  function formatTime(seconds) {
    const value = seconds || 0;
    return `${Math.floor(value / 60)}:${(value % 60).toString().padStart(2, '0')}`;
  }

  if (loading) return <div className="quiz-overlay"><div className="quiz-container"><div className="loading-message">Loading saved quiz session...</div></div></div>;
  if (error && !questions.length) return <div className="quiz-overlay"><div className="quiz-container"><div className="error-message">{error}</div><button className="text-button" type="button" onClick={loadQuiz}>Retry connection</button></div></div>;
  if (!questions.length) return <div className="quiz-overlay"><div className="quiz-container"><div className="loading-message">No questions found.</div></div></div>;

  const question = questions[Math.min(currentIdx, questions.length - 1)];
  const official = sessionType === 'official';
  const paused = connection !== 'active';
  const answeredCount = answers.length;
  return (
    <div className="quiz-overlay">
      <div className="quiz-container">
        <div className="quiz-header">
          <img src="/napcon_logo2.jpeg" alt="NAPCON 2026" className="quiz-logo" />
          <div>
            <p className="quiz-kicker">{official ? 'ONLINE ASSESSMENT' : 'PRACTICE ASSESSMENT'}</p>
            <h1 className="quiz-title">NAPCON PG Quiz</h1>
          </div>
          {official && <div className={`quiz-total-timer ${totalRemaining <= 60 ? 'warning' : ''}`} aria-label={`Total time remaining ${formatTime(totalRemaining)}`}>
            <span>Time remaining</span>
            <strong>{formatTime(totalRemaining)}</strong>
          </div>}
        </div>
        {paused && <div className="quiz-session-notice" role="status">{connection === 'blocked' ? 'This session was resumed on another device.' : connection === 'reconnecting' ? 'Connection lost. Quiz timers are paused while reconnecting…' : 'Quiz is paused. Reconnecting…'}</div>}
        <div className="quiz-topbar" aria-label="Assessment status">
          <div className="quiz-status-item">
            <span>Question</span>
            <strong>{currentIdx + 1} <small>of {questions.length}</small></strong>
          </div>
          <div className="quiz-status-item">
            <span>Answered</span>
            <strong>{answeredCount} <small>of {questions.length}</small></strong>
          </div>
          <div className={`question-timer ${questionRemaining <= 5 ? 'warning' : ''}`} aria-label={`Question time remaining ${formatTime(questionRemaining)}`}>
            <span>Question timer</span>
            <strong>{formatTime(questionRemaining)}</strong>
          </div>
        </div>
        <div className="progress-bar" role="progressbar" aria-label="Quiz progress" aria-valuemin="1" aria-valuemax={questions.length} aria-valuenow={Math.min(currentIdx + 1, questions.length)}><div className="progress-fill" style={{ width: `${(Math.min(currentIdx + 1, questions.length) / questions.length) * 100}%` }} /></div>
        <QuestionCard question={question} questionIndex={currentIdx} onAnswer={optionIndex => recordAnswer(currentIdx, optionIndex)} selectedAnswer={answers.find(answer => answer.questionIndex === currentIdx)?.answerIndex} disabled={paused || submitting} />
        {error && <div className="error-message">{error}</div>}
        <div className="quiz-actions">
          <p className={`answer-status ${answers.some(answer => answer.questionIndex === currentIdx) ? 'answered' : ''}`} aria-live="polite">{answers.some(answer => answer.questionIndex === currentIdx) ? 'Answer recorded' : 'Select one answer to continue'}</p>
          <button className="quiz-next-btn" type="button" onClick={() => currentIdx < questions.length - 1 ? advanceQuestion(false) : setConfirmation(true)} disabled={paused || submitting || !answers.some(answer => answer.questionIndex === currentIdx)}>
            {currentIdx < questions.length - 1 ? 'Save & Continue' : 'Submit'}
          </button>
        </div>
        <div className="scoring-info"><span className="score-rule correct-rule">Correct: +1</span><span className="score-rule wrong-rule">Wrong: 0</span><span className="score-rule unattempted-rule">Unattempted: 0</span></div>
        <div className="quiz-submit-area">
          <p id="submit-quiz-hint">Ready to finish? Submission is final.</p>
          <button className="quiz-submit-btn" type="button" aria-describedby="submit-quiz-hint" onClick={() => setConfirmation(true)} disabled={paused || submitting}>{submitting ? 'Submitting...' : 'Finish Quiz & Submit'}</button>
        </div>
      </div>
      {confirmation && (
        <div className="delete-dialog-overlay" role="presentation">
          <div className="delete-dialog quiz-confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="quiz-confirm-title" aria-describedby="quiz-confirm-message">
            <h3 id="quiz-confirm-title">Submit your quiz?</h3>
            <p className="delete-dialog-warning" id="quiz-confirm-message">Your saved answers will be submitted and this quiz cannot be attempted again.</p>
            <div className="delete-dialog-actions">
              <button className="dialog-cancel-btn" type="button" onClick={() => setConfirmation(false)} autoFocus>Cancel</button>
              <button className="dialog-submit-btn" type="button" onClick={() => { setConfirmation(false); saveAndSubmit(); }}>Save &amp; Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QuizPage;
